import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  Design,
  DesignerView,
  FengShuiConsultation,
  FengShuiRanking,
  Furniture,
  InteriorSegmentRef,
  Opening,
  Person,
  ProductCapture,
  Room,
  RoomSetting,
  Unit,
  Vec2,
  WallFinish,
  WallMount,
} from "./types";
import { distance } from "./geom";
import { lookById } from "./finishes/catalog";
import {
  applyLookTo,
  patchRoomFinishes,
  setRoomSettingIn,
  setWallFinishIn,
  type FinishesPatch,
} from "./finishes/edit";
import { remapWallFinishes } from "./finishes/wallKeys";
import { finishUndoSurface } from "./finishes/history";
import {
  clampPoseInRoom,
  clearInteriorGeometry,
  ensurePointIds,
  interiorLockKey,
  interiorSegments,
} from "./interiorWalls";
import { track } from "./telemetry";
import { endAdjustSession, noteUndo, startAdjustSession, type AdjustExitMethod } from "./transformTelemetry";
import { normalizeFurniture, resolveFurniture } from "./furniture/resolve";
import { wallLength } from "./wallGeom";
import {
  applyHistoryPatch,
  diffDesign,
  NUDGE_GESTURE_MS,
  rebaseGestureDesign,
  sameJson,
  type HistoryEntry,
  type HistoryPatch,
} from "./history";

export type { HistoryEntry } from "./history";

const STORAGE_KEY = "room-canvas:design:v2";
const STORAGE_VERSION = 5;

/**
 * Backfill optional collections and upgrade legacy furniture (pre-parametric
 * designs carried only `width/depth/color`) to carry an explicit `model`,
 * `material`, and `frontKind`. Mutates and returns `d`. Shared by the persist
 * `migrate` step and every load path (library, shared rooms) so a layout
 * saved at any point in the app's history renders identically.
 */
export function normalizeDesign(d: Design): Design {
  if (!Array.isArray(d.openings)) d.openings = [];
  if (!Array.isArray(d.consultations)) d.consultations = [];
  if (!Array.isArray(d.rankings)) d.rankings = [];
  if (Array.isArray(d.furniture)) {
    d.furniture = d.furniture.map((f) => normalizeFurniture(f));
  }
  if (d.room) {
    if (!Array.isArray(d.room.interiorWalls)) d.room.interiorWalls = [];
    if (!Array.isArray(d.room.interiorVertices)) d.room.interiorVertices = [];
    d.room = ensurePointIds(d.room);
  }
  return d;
}

/** Default width/height when applying a preset to a blank canvas (inches). */
export const DEFAULT_PRESET_ROOM_WIDTH_IN = 16 * 12;
export const DEFAULT_PRESET_ROOM_DEPTH_IN = 12 * 12;

/** True while the user hasn't committed a room outline yet (sketch or preset). */
export function isUnstartedRoom(room: Room): boolean {
  return room.points.length < 3;
}

/**
 * Emit `room_edited` for a committed room change and, when the change is
 * the one that takes the canvas from "no outline yet" to a real room,
 * also emit `room_created` — the funnel step that separates "looked at
 * the app" from "started designing".
 */
function trackRoomChange(prev: Room, next: Room, kind: "shape" | "points"): void {
  if (isUnstartedRoom(prev) && !isUnstartedRoom(next)) {
    if (kind === "shape" && next.shapeKind !== "custom") {
      track("room_created", { source: "shape", shape: next.shapeKind });
    } else {
      track("room_created", { source: "sketch" });
    }
  }
  track("room_edited", { kind });
}

export type BuildTool = "edit" | "sketch";

/** Active tool in the Design view. UI-only — NOT persisted. */
export type DesignTool = "select" | "measure";

/** Allowed grid-snap step sizes (inches), coarsest first. */
export const GRID_SNAP_OPTIONS_IN: readonly number[] = [1, 1 / 2, 1 / 3, 1 / 4];

/** Default snap step for fresh installs. */
export const DEFAULT_GRID_SNAP_IN = 1;

function isAllowedGridSnap(value: number): boolean {
  if (!Number.isFinite(value) || value <= 0) return false;
  return GRID_SNAP_OPTIONS_IN.some((opt) => Math.abs(opt - value) < 1e-6);
}

function uid(prefix = "id"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Offset applied on each paste so copies don't land exactly on top of the source. */
export const PASTE_OFFSET_IN = 8;

export type AddFurnitureOptions = {
  /** Defaults to true. Set false when inserting a clone during alt-drag. */
  select?: boolean;
  /** Analytics source for `furniture_added`. Defaults to `"library"`. */
  source?: "library" | "ai" | "import" | "duplicate";
};

export type AddMountOptions = {
  /** Defaults to true. Set false when inserting a clone during alt-drag. */
  select?: boolean;
};

export type DesignClipboard =
  | { kind: "furniture"; item: Omit<Furniture, "id"> }
  | { kind: "mount"; item: Omit<WallMount, "id"> };

/** Deep-clone a piece for the clipboard / duplicate, dropping identity and AI rationale. */
export function payloadWithoutId<T extends { id: string; rationale?: unknown }>(
  item: T
): Omit<T, "id"> {
  // `T & { id?: string }` still has a required `id`, so `delete clone.id`
  // is TS2790. Drop the keys on a plain record instead.
  const clone = structuredClone(item) as Record<string, unknown>;
  delete clone.id;
  delete clone.rationale;
  return clone as Omit<T, "id">;
}

/**
 * Per-profile latch backing the `furniture_first_placed` activation
 * event. Stored in `localStorage` (not in the Zustand persisted state)
 * because we want the event to fire exactly once per browser profile
 * even if the user wipes the in-flight design via `resetAll`.
 *
 * Returns true the first time it's called per profile; false thereafter.
 * Safe to call from non-browser contexts (returns true so unit tests
 * that exercise the addFurniture path still see the event).
 */
const FIRST_FURNITURE_KEY = "room-canvas:funnel:first-furniture-placed";
function shouldEmitFirstFurniturePlaced(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (window.localStorage.getItem(FIRST_FURNITURE_KEY)) return false;
    window.localStorage.setItem(FIRST_FURNITURE_KEY, String(Date.now()));
    return true;
  } catch {
    // Private-mode / quota: still fire the event but accept the risk
    // that it might re-fire on a subsequent session. Better signal
    // than silent loss.
    return true;
  }
}

function defaultRoom(): Room {
  return {
    id: uid("room"),
    name: "My Room",
    points: [],
    ceilingHeight: 8 * 12,
    wallThickness: 5,
    shapeKind: "rectangle",
  };
}

function defaultPerson(): Person {
  return {
    heightInches: 71, // 5'11"
    weightLbs: 170,
    position: null,
  };
}

function defaultDesign(): Design {
  return {
    room: defaultRoom(),
    furniture: [],
    mounts: [],
    openings: [],
    person: defaultPerson(),
    unit: "imperial",
    consultations: [],
    rankings: [],
  };
}

const HISTORY_CAP = 50;

export type GestureOptions = {
  /**
   * Pointer drags keep undo disabled until release so Cmd/Z cannot fire
   * mid-gesture. Text/number gestures omit this: undo commits the in-progress
   * edit, then steps.
   */
  blockUndo?: boolean;
};

/**
 * In-flight history gesture. Module-level so it isn't persisted and doesn't
 * trigger React re-renders. `depth` lets nested begin/end (e.g. a focused
 * NumberStepper wrapping a `batch`) share one patch at close.
 */
let historyGestureDepth = 0;
let historyGestureBlockUndo = false;
let historyGestureBaseline: Design | null = null;
/** Stepper +/- owns the open gesture and closes it after a quiet gap. */
let coalesceOwnsGesture = false;
let coalesceTimer: ReturnType<typeof setTimeout> | null = null;

function clearCoalesceTimer() {
  if (coalesceTimer != null) {
    clearTimeout(coalesceTimer);
    coalesceTimer = null;
  }
}

function discardHistoryGesture() {
  historyGestureDepth = 0;
  historyGestureBlockUndo = false;
  historyGestureBaseline = null;
  clearCoalesceTimer();
  coalesceOwnsGesture = false;
}

export interface FengShuiApply {
  /** New (x, y, rotation) for existing furniture by id, with the model's reason. */
  updates: { id: string; x: number; y: number; rotation: number; reason: string }[];
  /** Brand-new furniture to add, each tagged with the model's reason. */
  additions: (Omit<Furniture, "id"> & { reason: string })[];
  /** Furniture ids to remove, each with the model's reason (used for the log). */
  removals: { id: string; reason: string }[];
  /** New (wallIndex, alongWall, fromFloor) for existing mounts by id.
   *  Optional so callers that only operate on floor furniture don't have
   *  to pass empty arrays. */
  mountUpdates?: {
    id: string;
    wallIndex: number;
    alongWall: number;
    fromFloor: number;
    reason: string;
  }[];
  /** Brand-new wall mounts to hang, each tagged with the model's reason. */
  mountAdditions?: (Omit<WallMount, "id"> & { reason: string })[];
  /** Mount ids to remove, each with the model's reason. */
  mountRemovals?: { id: string; reason: string }[];
  /** Context for the consultation entry the store will append. */
  consultation: {
    mode: FengShuiConsultation["mode"];
    notes?: string;
    overall: string;
    filtered?: FengShuiConsultation["filtered"];
  };
  /**
   * The feng shui first run's saved example, not a generated layout.
   * Reported as `feng_shui_example_applied` instead of `ai_layout_applied`
   * so real AI metrics stay clean.
   */
  example?: boolean;
}

interface State {
  /** The current design. */
  design: Design;
  /**
   * If the active design was loaded from a saved room in the library, the
   * SavedRoom id it came from. Used so the UI can offer "Update saved copy"
   * vs. "Save as new". Cleared by `resetAll` and by editing a brand-new
   * design from scratch.
   */
  activeSavedRoomId: string | null;
  /**
   * If the active design is a multiplayer-shared room, the SharedRoom id.
   * When non-null:
   *   - The sync orchestrator (sharedRoomSync.ts) is hooked up and
   *     forwarding outbound mutations to Firestore.
   *   - History records field-level patches so undo reverts this user's
   *     edit without wiping a collaborator's unrelated changes.
   *   - The library "Save / Update" UI hides; "Save a copy" appears in
   *     its place.
   * Mutually exclusive with `activeSavedRoomId`.
   */
  activeSharedRoomId: string | null;
  /**
   * IDs the local user is currently dragging. While set, the sync
   * orchestrator suppresses Firestore writes for these items — their live
   * positions stream through RTDB instead, and a single Firestore commit
   * fires on drag-end. Not persisted (cleared on reload).
   */
  draggingFurnitureId: string | null;
  draggingMountId: string | null;
  draggingOpeningId: string | null;
  /** Active view in the editor. */
  view: DesignerView;
  /** Wall index currently inspected in 'wall' view. Null when an interior wall is active. */
  activeWallIndex: number | null;
  /** Interior segment inspected in 'wall' view. */
  activeInteriorWall: InteriorSegmentRef | null;
  /** Selected furniture id (in 'design' view). */
  selectedFurnitureId: string | null;
  /** Selected wall mount id (in 'wall' view). */
  selectedMountId: string | null;
  /** Selected opening id (in 'wall' view's Openings tab). */
  selectedOpeningId: string | null;
  /** Selected wall/vertex while editing room shape (build view). */
  selectedRoomWallIndex: number | null;
  selectedRoomVertexIndex: number | null;
  selectedInteriorWall: InteriorSegmentRef | null;
  selectedInteriorVertexId: string | null;
  /** Active tool in the Build view. UI-only — NOT persisted. */
  buildTool: BuildTool;
  /** Active tool in the Design view. UI-only — NOT persisted. */
  designTool: DesignTool;
  /** Cleaned sketch polygon awaiting Use / Redraw confirmation. UI-only. */
  pendingSketch: Vec2[] | null;
  /** Id of the modular sectional whose modules are being edited on the
   *  canvas (the "Edit modules" mode). UI-only — NOT persisted. */
  editingModulesId: string | null;
  /** Id of the piece in the touch Adjust mode (resize / rotate knobs).
   *  UI-only — NOT persisted, not in history. */
  adjustingFurnitureId: string | null;
  /** Whether grid snapping is on. */
  snapEnabled: boolean;
  /** Snap step (inches) when `snapEnabled` is true. One of {@link GRID_SNAP_OPTIONS_IN}. */
  gridSnapInches: number;
  /** Align a dragged piece's edges/centers to other furniture. */
  furnitureSnapEnabled: boolean;
  /** Show person silhouette. */
  showPerson: boolean;
  /** Undo / redo stacks. In-memory only — not persisted. */
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
  /**
   * True while an input gesture (typing, stepper) has mutated the design
   * but hasn't closed yet. Lets the Undo button light up before blur.
   */
  historyPending: boolean;
  /**
   * In-memory copy/paste buffer. UI-only — not persisted. Survives view
   * switches so a piece copied in Design can be pasted after a detour,
   * but is wiped by `resetAll` / a full reload.
   */
  clipboard: DesignClipboard | null;

  /** Mutators. */
  setView: (v: DesignerView) => void;
  setUnit: (u: Unit) => void;
  setRoom: (r: Room) => void;
  setRoomPoints: (points: Vec2[]) => void;
  /** Set points without pushing to history (use during a drag). Marks shape as custom. */
  setRoomPointsLive: (points: Vec2[]) => void;
  /** Replace room geometry (and optionally openings/mounts) in one history step. */
  applyRoomChange: (
    room: Room,
    openings?: Opening[],
    mounts?: WallMount[]
  ) => void;
  /** Live room update during a drag. Marks the shape as custom. */
  setRoomLive: (room: Room) => void;
  /** Snapshot the current design into history (call once at drag start). */
  beginRoomEdit: () => void;
  setRoomName: (name: string) => void;
  setCeilingHeight: (h: number) => void;
  /** Patch the room's finishes (`null` resets a field to inferred). One undo entry. */
  setRoomFinishes: (patch: FinishesPatch) => void;
  /** One wall's finish by wall key; `null` drops the override. One undo entry. */
  setWallFinish: (wallKey: string, finish: WallFinish | null) => void;
  /** Floor, walls, ceiling and trim from a curated Look. One undo entry. */
  applyLook: (lookId: string) => void;
  setRoomSetting: (setting: RoomSetting) => void;
  selectRoomWall: (i: number | null) => void;
  selectRoomVertex: (i: number | null) => void;
  selectInteriorWall: (ref: InteriorSegmentRef | null) => void;
  selectInteriorVertex: (id: string | null) => void;
  setBuildTool: (t: BuildTool) => void;
  setDesignTool: (t: DesignTool) => void;
  setPendingSketch: (p: Vec2[] | null) => void;
  setEditingModules: (id: string | null) => void;
  /** Open Adjust mode on a piece (selects it). */
  enterAdjustMode: (id: string) => void;
  /** Leave Adjust mode; the piece stays selected. */
  exitAdjustMode: (method: AdjustExitMethod) => void;
  /** Pin a wall's length so subsequent edits to other walls try to preserve
   *  it. Passing the wall's current length is the typical caller pattern
   *  (auto-lock on user input). */
  lockWall: (index: number, length: number) => void;
  /** Drop the lock on a single wall. No-op if not locked. */
  unlockWall: (index: number) => void;
  lockInteriorSegment: (wallId: string, segment: number, length: number) => void;
  unlockInteriorSegment: (wallId: string, segment: number) => void;
  /** Drop all wall locks at once. Called when the polygon's topology changes
   *  (corner add/remove, preset, sketch commit) since indices become stale. */
  clearWallLocks: () => void;
  /** Sync stored lock values to the polygon's current edge lengths. Called at
   *  the end of a wall/vertex drag so the lock semantics survive the manual
   *  reshape: previously-locked walls stay locked, but at their new lengths. */
  syncWallLocksToCurrent: () => void;

  addFurniture: (f: Omit<Furniture, "id">, opts?: AddFurnitureOptions) => string;
  updateFurniture: (id: string, patch: Partial<Furniture>) => void;
  /**
   * Give every placed piece of a product that has no mesh yet the one that
   * just finished building. Not an undo step: the copies in the undo and
   * redo stacks (and an open gesture's baseline) get it too, so undoing
   * the placement and redoing it keeps the mesh.
   */
  attachProductCapture: (productId: string, capture: ProductCapture) => void;
  /**
   * Move several pieces in one commit (an animation frame). Rationale is
   * left alone: the caller (the feng shui example player) stamps it when
   * the sequence lands via `applyFengShuiSuggestion`. Wrap the whole
   * sequence in `beginGesture` / `endGesture` to get one undo entry.
   */
  setFurniturePoses: (poses: { id: string; x: number; y: number; rotation: number }[]) => void;
  removeFurniture: (id: string) => void;
  selectFurniture: (id: string | null) => void;
  /** Reorder a furniture piece relative to the others. The array order
   *  doubles as Z-order on the canvas: later items render on top. */
  reorderFurniture: (
    id: string,
    direction: "forward" | "backward" | "front" | "back"
  ) => void;

  setActiveWallIndex: (i: number | null) => void;
  setActiveInteriorWall: (ref: InteriorSegmentRef | null) => void;
  addMount: (m: Omit<WallMount, "id">, opts?: AddMountOptions) => string;
  updateMount: (id: string, patch: Partial<WallMount>) => void;
  removeMount: (id: string) => void;
  selectMount: (id: string | null) => void;

  addOpening: (o: Omit<Opening, "id">) => string;
  updateOpening: (id: string, patch: Partial<Opening>) => void;
  removeOpening: (id: string) => void;
  selectOpening: (id: string | null) => void;

  /** Apply an AI feng shui suggestion in a single history entry. */
  /** Returns the id of the consultation it appended. */
  applyFengShuiSuggestion: (suggestion: FengShuiApply) => string;

  /** Drop a stored consultation from the design's log. Does NOT clear
   *  per-piece `rationale` (those tags are self-contained snapshots). */
  removeConsultation: (id: string) => void;

  /** Append a feng shui ranking to the design's ranking log. */
  appendRanking: (ranking: FengShuiRanking) => void;

  /** Drop a stored ranking from the design's log. */
  removeRanking: (id: string) => void;

  /** Furniture ids the user is currently "flashing" (e.g. via the
   *  History sheet's "Highlight on canvas" button). The canvas can read
   *  this to glow the matching pieces; auto-clears via `flashFurnitureIds`. */
  flashedFurnitureIds: string[];
  /** Briefly highlight the given furniture ids on the canvas. */
  flashFurnitureIds: (ids: string[], durationMs?: number) => void;

  setPerson: (p: Partial<Person>) => void;
  toggleSnap: () => void;
  setGridSnapInches: (n: number) => void;
  toggleFurnitureSnap: () => void;
  togglePerson: () => void;

  /**
   * Open a history gesture so subsequent design mutations share one undo
   * step (drags, typing, arrow-key bursts). Nested calls are counted.
   */
  beginGesture: (opts?: GestureOptions) => void;
  /** Close a matching {@link beginGesture} and record one patch if anything changed. */
  endGesture: () => void;
  /**
   * Close a matching {@link beginGesture} and throw its changes away: the
   * design snaps back to the gesture's baseline and no undo entry is
   * recorded. Used when a pinch interrupts a drag. Inside a nested gesture
   * it only closes its level, since the outer one's edits can't be split off.
   */
  cancelGesture: () => void;
  /** Run `fn` inside a history gesture so its mutations are one undo step. */
  batch: (fn: () => void) => void;
  /**
   * Run `fn` now, and if nothing else has a gesture open, close it after
   * {@link NUDGE_GESTURE_MS} of quiet so a burst of stepper clicks is one
   * undo step. An already-open gesture (typing) keeps ownership and closes
   * itself.
   */
  coalesceEdit: (fn: () => void) => void;
  undo: () => void;
  redo: () => void;

  /**
   * Apply a remotely-arrived design without touching history. Rebases an
   * open gesture so the patch at close stays local-only.
   */
  applyRemoteDesign: (design: Design) => void;

  /** Snapshot the selected furniture or mount into {@link clipboard}. */
  copySelection: () => boolean;
  /** Paste the clipboard contents with an 8" cascade offset. */
  pasteClipboard: () => boolean;

  /**
   * Replace the active design wholesale (e.g. when loading a saved room
   * from the library). Clears selection, undo/redo, and view-local state.
   * If `savedRoomId` is provided, the design is treated as the active copy
   * of that saved room.
   */
  loadDesign: (design: Design, savedRoomId?: string | null) => void;
  /** Mark the active design as no longer linked to a saved room. */
  clearActiveSavedRoom: () => void;
  /**
   * Replace the active design with a multiplayer-shared room. Clears
   * undo/redo, sets `activeSharedRoomId`, and clears `activeSavedRoomId`.
   * The sync orchestrator (started separately) wires this state to
   * Firestore.
   */
  loadSharedDesign: (design: Design, sharedRoomId: string) => void;
  /** Tear down the shared-room link (e.g. when leaving / unloading). */
  clearActiveSharedRoom: () => void;

  /** Mark a furniture item as actively being dragged locally. The sync
   *  orchestrator uses this to skip Firestore writes for the dragging
   *  item — RTDB drag-preview takes over until release. */
  setDraggingFurniture: (id: string | null) => void;
  setDraggingMount: (id: string | null) => void;
  setDraggingOpening: (id: string | null) => void;

  resetAll: () => void;
}

export const useDesign = create<State>()(
  persist(
    (set, get) => {
      type DesignExtra = Partial<
        Pick<
          State,
          | "selectedFurnitureId"
          | "selectedMountId"
          | "selectedOpeningId"
          | "editingModulesId"
          | "adjustingFurnitureId"
        >
      >;

      /** Clear Adjust mode, reporting how it ended. Returns the new value. */
      function leaveAdjust(method: AdjustExitMethod): null {
        if (get().adjustingFurnitureId) endAdjustSession(method);
        return null;
      }

      function commitOpenGesture() {
        while (historyGestureDepth > 0) closeGesture();
      }

      function closeGesture() {
        if (historyGestureDepth <= 0) return;
        historyGestureDepth--;
        if (historyGestureDepth > 0) return;
        clearCoalesceTimer();
        coalesceOwnsGesture = false;
        const baseline = historyGestureBaseline;
        historyGestureBaseline = null;
        historyGestureBlockUndo = false;
        if (!baseline) {
          set({ historyPending: false });
          return;
        }
        const entry = diffDesign(baseline, get().design);
        if (!entry) {
          set({ historyPending: false });
          return;
        }
        const { undoStack } = get();
        set({
          undoStack: [
            ...undoStack.slice(-(HISTORY_CAP - 1)),
            entry,
          ],
          redoStack: [],
          historyPending: false,
        });
      }

      function commitDesign(nextDesign: Design, extra?: DesignExtra) {
        const prev = get().design;
        if (sameJson(prev, nextDesign)) {
          if (extra) set(extra);
          return;
        }
        if (historyGestureDepth > 0) {
          if (!historyGestureBaseline) {
            historyGestureBaseline = structuredClone(prev);
          }
          set({
            design: nextDesign,
            historyPending: !historyGestureBlockUndo,
            ...extra,
          });
          return;
        }
        const entry = diffDesign(prev, nextDesign);
        if (!entry) {
          set({ design: nextDesign, ...extra });
          return;
        }
        const { undoStack } = get();
        set({
          design: nextDesign,
          undoStack: [
            ...undoStack.slice(-(HISTORY_CAP - 1)),
            entry,
          ],
          redoStack: [],
          historyPending: false,
          ...extra,
        });
      }

      function survivingSelection(design: Design) {
        const s = get();
        const nPoints = design.room.points.length;
        return {
          selectedFurnitureId:
            s.selectedFurnitureId &&
            design.furniture.some((f) => f.id === s.selectedFurnitureId)
              ? s.selectedFurnitureId
              : null,
          selectedMountId:
            s.selectedMountId &&
            design.mounts.some((m) => m.id === s.selectedMountId)
              ? s.selectedMountId
              : null,
          selectedOpeningId:
            s.selectedOpeningId &&
            (design.openings ?? []).some((o) => o.id === s.selectedOpeningId)
              ? s.selectedOpeningId
              : null,
          editingModulesId:
            s.editingModulesId &&
            design.furniture.some((f) => f.id === s.editingModulesId)
              ? s.editingModulesId
              : null,
          adjustingFurnitureId:
            s.adjustingFurnitureId &&
            design.furniture.some((f) => f.id === s.adjustingFurnitureId)
              ? s.adjustingFurnitureId
              : leaveAdjust("deleted"),
          selectedRoomWallIndex:
            s.selectedRoomWallIndex != null &&
            s.selectedRoomWallIndex < nPoints
              ? s.selectedRoomWallIndex
              : null,
          selectedRoomVertexIndex:
            s.selectedRoomVertexIndex != null &&
            s.selectedRoomVertexIndex < nPoints
              ? s.selectedRoomVertexIndex
              : null,
          selectedInteriorWall: (() => {
            const ref = s.selectedInteriorWall;
            if (!ref) return null;
            const wall = (design.room.interiorWalls ?? []).find((w) => w.id === ref.wallId);
            if (!wall || ref.segment < 0 || ref.segment >= wall.vertexIds.length - 1) {
              return null;
            }
            return ref;
          })(),
          selectedInteriorVertexId: (() => {
            const id = s.selectedInteriorVertexId;
            if (!id) return null;
            if ((design.room.pointIds ?? []).includes(id)) return id;
            if ((design.room.interiorVertices ?? []).some((v) => v.id === id)) return id;
            return null;
          })(),
          activeInteriorWall: (() => {
            const ref = s.activeInteriorWall;
            if (!ref) return null;
            const wall = (design.room.interiorWalls ?? []).find((w) => w.id === ref.wallId);
            if (!wall || ref.segment < 0 || ref.segment >= wall.vertexIds.length - 1) {
              return null;
            }
            return ref;
          })(),
        };
      }

      return {
      design: defaultDesign(),
      activeSavedRoomId: null,
      activeSharedRoomId: null,
      draggingFurnitureId: null,
      draggingMountId: null,
      draggingOpeningId: null,
      view: "build",
      activeWallIndex: null,
      activeInteriorWall: null,
      selectedFurnitureId: null,
      selectedMountId: null,
      selectedOpeningId: null,
      selectedRoomWallIndex: null,
      selectedRoomVertexIndex: null,
      selectedInteriorWall: null,
      selectedInteriorVertexId: null,
      buildTool: isUnstartedRoom(defaultDesign().room) ? "sketch" : "edit",
      designTool: "select",
      pendingSketch: null,
      editingModulesId: null,
      adjustingFurnitureId: null,
      snapEnabled: true,
      gridSnapInches: DEFAULT_GRID_SNAP_IN,
      furnitureSnapEnabled: true,
      showPerson: true,
      undoStack: [],
      redoStack: [],
      historyPending: false,
      clipboard: null,
      flashedFurnitureIds: [],

      setView: (view) => {
        commitOpenGesture();
        const prev = get().view;
        if (prev !== view) {
          track("view_changed", { from: prev, to: view });
        }
        set({
          view,
          selectedFurnitureId: null,
          selectedMountId: null,
          selectedOpeningId: null,
          selectedRoomWallIndex: null,
          selectedRoomVertexIndex: null,
          selectedInteriorWall: null,
          selectedInteriorVertexId: null,
          // Pending sketches are scoped to the Build view — drop them if the
          // user navigates away so we never commit a stale ghost.
          pendingSketch: null,
          designTool: "select",
          editingModulesId: null,
          adjustingFurnitureId: prev !== view ? leaveAdjust("view") : get().adjustingFurnitureId,
        });
      },
      setUnit: (unit) => {
        const { design } = get();
        if (design.unit === unit) return;
        commitDesign({ ...design, unit });
      },

      setRoom: (input) => {
        const { design } = get();
        const room = remapWallFinishes(design.room, input);
        if (sameJson(design.room, room)) return;
        commitDesign({ ...design, room });
        trackRoomChange(design.room, room, "shape");
      },
      setRoomPoints: (points) => {
        const { design } = get();
        if (sameJson(design.room.points, points)) return;
        const sameLength = design.room.points.length === points.length;
        if (!sameLength) {
          const cleared = clearInteriorGeometry(
            design.room,
            design.openings,
            design.mounts,
            points
          );
          commitDesign({
            ...design,
            room: remapWallFinishes(design.room, cleared.room),
            openings: cleared.openings,
            mounts: cleared.mounts,
          });
        } else {
          commitDesign({ ...design, room: { ...design.room, points } });
        }
        trackRoomChange(design.room, get().design.room, "points");
      },
      setRoomPointsLive: (points) => {
        const { design } = get();
        const nextRoom = { ...design.room, points, shapeKind: "custom" as const };
        if (sameJson(design.room, nextRoom)) return;
        const nextDesign = { ...design, room: nextRoom };
        if (historyGestureDepth > 0) commitDesign(nextDesign);
        else set({ design: nextDesign });
      },
      applyRoomChange: (input, openings, mounts) => {
        const { design } = get();
        const room = remapWallFinishes(design.room, input);
        commitDesign({
          ...design,
          room,
          openings: openings ?? design.openings,
          mounts: mounts ?? design.mounts,
        });
        trackRoomChange(design.room, room, "shape");
      },
      setRoomLive: (input) => {
        const { design } = get();
        const nextRoom = { ...remapWallFinishes(design.room, input), shapeKind: "custom" as const };
        if (sameJson(design.room, nextRoom)) return;
        const nextDesign = { ...design, room: nextRoom };
        if (historyGestureDepth > 0) commitDesign(nextDesign);
        else set({ design: nextDesign });
      },
      beginRoomEdit: () => {
        get().beginGesture({ blockUndo: true });
      },
      setRoomName: (name) => {
        const { design } = get();
        if (design.room.name === name) return;
        commitDesign({
          ...design,
          room: { ...design.room, name },
        });
      },
      setCeilingHeight: (h) => {
        const { design } = get();
        if (design.room.ceilingHeight === h) return;
        commitDesign({
          ...design,
          room: { ...design.room, ceilingHeight: h },
        });
      },
      setRoomFinishes: (patch) => {
        const { design } = get();
        const room = patchRoomFinishes(design.room, patch);
        if (room !== design.room) commitDesign({ ...design, room });
      },
      setWallFinish: (wallKey, finish) => {
        const { design } = get();
        const room = setWallFinishIn(design.room, wallKey, finish);
        if (room !== design.room) commitDesign({ ...design, room });
      },
      applyLook: (lookId) => {
        const look = lookById(lookId);
        if (!look) return;
        const { design } = get();
        const room = applyLookTo(design.room, look);
        if (room !== design.room) commitDesign({ ...design, room });
      },
      setRoomSetting: (setting) => {
        const { design } = get();
        const room = setRoomSettingIn(design.room, setting);
        if (room !== design.room) commitDesign({ ...design, room });
      },
      selectRoomWall: (i) =>
        set({
          selectedRoomWallIndex: i,
          selectedRoomVertexIndex: null,
          selectedInteriorWall: null,
          selectedInteriorVertexId: null,
        }),
      selectRoomVertex: (i) =>
        set({
          selectedRoomVertexIndex: i,
          selectedRoomWallIndex: null,
          selectedInteriorWall: null,
          selectedInteriorVertexId: null,
        }),
      selectInteriorWall: (ref) =>
        set({
          selectedInteriorWall: ref,
          selectedInteriorVertexId: null,
          selectedRoomWallIndex: null,
          selectedRoomVertexIndex: null,
        }),
      selectInteriorVertex: (id) =>
        set({
          selectedInteriorVertexId: id,
          selectedInteriorWall: null,
          selectedRoomWallIndex: null,
          selectedRoomVertexIndex: null,
        }),
      setBuildTool: (buildTool) =>
        set({
          buildTool,
          // Switching tools always clears any in-flight ghost so we don't
          // leak a stale confirm bar between modes.
          pendingSketch: null,
        }),
      setDesignTool: (designTool) =>
        set((s) => ({
          designTool,
          // Measurement mode and furniture selection fight for clicks.
          selectedFurnitureId:
            designTool === "measure" ? null : s.selectedFurnitureId,
          adjustingFurnitureId:
            designTool === "measure" ? leaveAdjust("canvas") : s.adjustingFurnitureId,
        })),
      setPendingSketch: (pendingSketch) => set({ pendingSketch }),
      setEditingModules: (id) =>
        set((s) => ({
          editingModulesId: id,
          adjustingFurnitureId: id ? leaveAdjust("other_piece") : s.adjustingFurnitureId,
          // Entering module edit mode selects the piece; leaving keeps it.
          selectedFurnitureId: id ?? s.selectedFurnitureId,
          selectedMountId: id ? null : s.selectedMountId,
        })),

      enterAdjustMode: (id) => {
        if (!get().design.furniture.some((f) => f.id === id)) return;
        if (get().adjustingFurnitureId === id) return;
        leaveAdjust("other_piece");
        startAdjustSession();
        set({
          adjustingFurnitureId: id,
          selectedFurnitureId: id,
          selectedMountId: null,
          editingModulesId: null,
        });
      },
      exitAdjustMode: (method) => {
        if (!get().adjustingFurnitureId) return;
        set({ adjustingFurnitureId: leaveAdjust(method) });
      },

      lockWall: (index, length) => {
        const { design } = get();
        if (index < 0 || index >= design.room.points.length) return;
        if (!Number.isFinite(length) || length <= 0) return;
        const cur = design.room.lockedWallLengths ?? {};
        if (cur[index] === length) return;
        commitDesign({
          ...design,
          room: {
            ...design.room,
            lockedWallLengths: {
              ...cur,
              [index]: length,
            },
          },
        });
      },
      unlockWall: (index) => {
        const { design } = get();
        const cur = design.room.lockedWallLengths;
        if (!cur || !(index in cur)) return;
        const next = { ...cur };
        delete next[index];
        commitDesign({
          ...design,
          room: { ...design.room, lockedWallLengths: next },
        });
      },
      lockInteriorSegment: (wallId, segment, length) => {
        const { design } = get();
        if (!Number.isFinite(length) || length <= 0) return;
        const key = interiorLockKey(wallId, segment);
        const cur = design.room.lockedInteriorLengths ?? {};
        if (cur[key] === length) return;
        commitDesign({
          ...design,
          room: {
            ...design.room,
            lockedInteriorLengths: { ...cur, [key]: length },
          },
        });
      },
      unlockInteriorSegment: (wallId, segment) => {
        const { design } = get();
        const cur = design.room.lockedInteriorLengths;
        const key = interiorLockKey(wallId, segment);
        if (!cur || !(key in cur)) return;
        const next = { ...cur };
        delete next[key];
        commitDesign({
          ...design,
          room: { ...design.room, lockedInteriorLengths: next },
        });
      },
      clearWallLocks: () => {
        const { design } = get();
        if (!design.room.lockedWallLengths) return;
        const nextRoom = { ...design.room };
        delete nextRoom.lockedWallLengths;
        commitDesign({ ...design, room: nextRoom });
      },
      syncWallLocksToCurrent: () => {
        const { design } = get();
        const locks = design.room.lockedWallLengths;
        const interiorLocks = design.room.lockedInteriorLengths;
        let nextLocks = locks;
        let nextInterior = interiorLocks;
        if (locks && Object.keys(locks).length > 0) {
          const pts = design.room.points;
          const n = pts.length;
          const next: Record<number, number> = {};
          for (const k of Object.keys(locks)) {
            const idx = Number(k);
            if (idx < 0 || idx >= n) continue;
            const a = pts[idx];
            const b = pts[(idx + 1) % n];
            next[idx] = distance(a, b);
          }
          if (!sameJson(locks, next)) nextLocks = next;
        }
        if (interiorLocks && Object.keys(interiorLocks).length > 0) {
          const next: Record<string, number> = {};
          for (const seg of interiorSegments(design.room)) {
            if (!seg.wallId || seg.segment == null) continue;
            const key = interiorLockKey(seg.wallId, seg.segment);
            if (key in interiorLocks) next[key] = seg.len;
          }
          if (!sameJson(interiorLocks, next)) nextInterior = next;
        }
        if (nextLocks === locks && nextInterior === interiorLocks) return;
        const room = { ...design.room };
        if (nextLocks && Object.keys(nextLocks).length > 0) room.lockedWallLengths = nextLocks;
        else delete room.lockedWallLengths;
        if (nextInterior && Object.keys(nextInterior).length > 0) {
          room.lockedInteriorLengths = nextInterior;
        } else {
          delete room.lockedInteriorLengths;
        }
        commitDesign({ ...design, room });
      },

      addFurniture: (f, opts) => {
        const wasEmpty = get().design.furniture.length === 0;
        const id = uid("f");
        const item = normalizeFurniture({ ...f, id });
        const isFloorCovering =
          item.model?.kind === "parametric" && item.model.template === "rug";
        const select = opts?.select !== false;
        const { design } = get();
        commitDesign(
          {
            ...design,
            furniture: isFloorCovering
              ? [item, ...design.furniture]
              : [...design.furniture, item],
          },
          { selectedFurnitureId: select ? id : get().selectedFurnitureId }
        );
        track("furniture_added", {
          preset_name: f.name,
          source: opts?.source ?? "library",
        });
        if (wasEmpty && shouldEmitFirstFurniturePlaced()) {
          track("furniture_first_placed", { preset_name: f.name });
        }
        return id;
      },
      updateFurniture: (id, patch) => {
        const { design } = get();
        const item = design.furniture.find((f) => f.id === id);
        if (!item) return;
        const invalidatesRationale =
          patch.x !== undefined ||
          patch.y !== undefined ||
          patch.rotation !== undefined;
        const next = { ...item, ...patch };
        if (invalidatesRationale && next.rationale) delete next.rationale;
        if (sameJson(item, next)) return;
        commitDesign({
          ...design,
          furniture: design.furniture.map((f) => (f.id === id ? next : f)),
        });
      },
      attachProductCapture: (productId, capture) => {
        const needs = (f: Furniture) => f.productId === productId && !f.capture;
        const attach = (f: Furniture): Furniture => (needs(f) ? { ...f, capture: { ...capture } } : f);
        const attachPatch = (patch: HistoryPatch): HistoryPatch =>
          patch.furniture?.some((op) => op.op !== "update" && needs(op.item))
            ? {
                ...patch,
                furniture: patch.furniture.map((op) =>
                  op.op === "update" ? op : { ...op, item: attach(op.item) }
                ),
              }
            : patch;
        const attachEntry = (entry: HistoryEntry): HistoryEntry => {
          const undo = attachPatch(entry.undo);
          const redo = attachPatch(entry.redo);
          return undo === entry.undo && redo === entry.redo ? entry : { undo, redo };
        };
        if (historyGestureBaseline?.furniture.some(needs)) {
          historyGestureBaseline = {
            ...historyGestureBaseline,
            furniture: historyGestureBaseline.furniture.map(attach),
          };
        }
        const { design, undoStack, redoStack } = get();
        const nextUndo = undoStack.map(attachEntry);
        const nextRedo = redoStack.map(attachEntry);
        const stacksChanged =
          nextUndo.some((e, i) => e !== undoStack[i]) || nextRedo.some((e, i) => e !== redoStack[i]);
        const designChanged = design.furniture.some(needs);
        if (!designChanged && !stacksChanged) return;
        set({
          design: designChanged ? { ...design, furniture: design.furniture.map(attach) } : design,
          undoStack: nextUndo,
          redoStack: nextRedo,
        });
      },
      setFurniturePoses: (poses) => {
        const { design } = get();
        const byId = new Map(poses.map((p) => [p.id, p] as const));
        let changed = false;
        const furniture = design.furniture.map((f) => {
          const p = byId.get(f.id);
          if (!p || (f.x === p.x && f.y === p.y && f.rotation === p.rotation)) return f;
          changed = true;
          return { ...f, x: p.x, y: p.y, rotation: p.rotation };
        });
        if (!changed) return;
        commitDesign({ ...design, furniture });
      },
      removeFurniture: (id) => {
        const { design } = get();
        const removed = design.furniture.find((f) => f.id === id);
        if (!removed) return;
        commitDesign(
          {
            ...design,
            furniture: design.furniture.filter((f) => f.id !== id),
          },
          {
            selectedFurnitureId: null,
            editingModulesId:
              get().editingModulesId === id ? null : get().editingModulesId,
            adjustingFurnitureId:
              get().adjustingFurnitureId === id ? leaveAdjust("deleted") : get().adjustingFurnitureId,
          }
        );
        track("furniture_deleted", { preset_name: removed.name });
      },
      selectFurniture: (id) =>
        set((s) => ({
          selectedFurnitureId: id,
          selectedMountId: null,
          // Selecting a different piece (or nothing) leaves module editing
          // and Adjust mode.
          editingModulesId: s.editingModulesId === id ? s.editingModulesId : null,
          adjustingFurnitureId:
            s.adjustingFurnitureId === id
              ? s.adjustingFurnitureId
              : leaveAdjust(id ? "other_piece" : "canvas"),
        })),
      reorderFurniture: (id, direction) => {
        const list = get().design.furniture;
        const idx = list.findIndex((f) => f.id === id);
        if (idx < 0) return;
        let nextIdx: number;
        switch (direction) {
          case "forward":
            if (idx === list.length - 1) return;
            nextIdx = idx + 1;
            break;
          case "backward":
            if (idx === 0) return;
            nextIdx = idx - 1;
            break;
          case "front":
            if (idx === list.length - 1) return;
            nextIdx = list.length - 1;
            break;
          case "back":
            if (idx === 0) return;
            nextIdx = 0;
            break;
        }
        const next = list.slice();
        const [item] = next.splice(idx, 1);
        next.splice(nextIdx, 0, item);
        commitDesign({ ...get().design, furniture: next });
      },

      setActiveWallIndex: (i) =>
        set({
          activeWallIndex: i,
          activeInteriorWall: null,
          selectedMountId: null,
          selectedOpeningId: null,
        }),
      setActiveInteriorWall: (ref) =>
        set({
          activeInteriorWall: ref,
          activeWallIndex: null,
          selectedMountId: null,
          selectedOpeningId: null,
        }),

      addMount: (m, opts) => {
        const id = uid("m");
        const select = opts?.select !== false;
        const { design } = get();
        commitDesign(
          { ...design, mounts: [...design.mounts, { ...m, id }] },
          { selectedMountId: select ? id : get().selectedMountId }
        );
        track("mount_changed", { action: "added", category: m.category });
        return id;
      },
      updateMount: (id, patch) => {
        const { design } = get();
        const item = design.mounts.find((m) => m.id === id);
        if (!item) return;
        const invalidatesRationale =
          patch.wallIndex !== undefined ||
          patch.alongWall !== undefined ||
          patch.fromFloor !== undefined;
        const next = { ...item, ...patch };
        if (invalidatesRationale && next.rationale) delete next.rationale;
        if (sameJson(item, next)) return;
        commitDesign({
          ...design,
          mounts: design.mounts.map((m) => (m.id === id ? next : m)),
        });
      },
      removeMount: (id) => {
        const { design } = get();
        const removed = design.mounts.find((m) => m.id === id);
        if (!removed) return;
        commitDesign(
          {
            ...design,
            mounts: design.mounts.filter((m) => m.id !== id),
          },
          { selectedMountId: null }
        );
        track("mount_changed", { action: "removed", category: removed.category });
      },
      selectMount: (id) =>
        set({ selectedMountId: id, selectedFurnitureId: null, selectedOpeningId: null }),

      addOpening: (o) => {
        const id = uid("o");
        const { design } = get();
        commitDesign(
          { ...design, openings: [...design.openings, { ...o, id }] },
          {
            selectedOpeningId: id,
            selectedMountId: null,
            selectedFurnitureId: null,
          }
        );
        track("opening_changed", { action: "added", kind: o.kind });
        if (o.style === "gate") track("gate_added", { source: get().view === "wall" ? "walls" : "design" });
        return id;
      },
      updateOpening: (id, patch) => {
        const { design } = get();
        const item = design.openings.find((o) => o.id === id);
        if (!item) return;
        const next = { ...item, ...patch };
        if (sameJson(item, next)) return;
        commitDesign({
          ...design,
          openings: design.openings.map((o) => (o.id === id ? next : o)),
        });
      },
      removeOpening: (id) => {
        const { design } = get();
        const removed = design.openings.find((o) => o.id === id);
        if (!removed) return;
        commitDesign(
          {
            ...design,
            openings: design.openings.filter((o) => o.id !== id),
          },
          { selectedOpeningId: null }
        );
        track("opening_changed", { action: "removed", kind: removed.kind });
      },
      selectOpening: (id) =>
        set({ selectedOpeningId: id, selectedFurnitureId: null, selectedMountId: null }),

      applyFengShuiSuggestion: (suggestion) => {
        const design = get().design;
        const consultationId = uid("c");
        const at = Date.now();

        const removeMap = new Map(
          suggestion.removals.map((r) => [r.id, r] as const)
        );
        const updateMap = new Map(
          suggestion.updates.map((u) => [u.id, u] as const)
        );

        const nameById = new Map(
          design.furniture.map((f) => [f.id, f.name] as const)
        );
        const mountNameById = new Map(
          design.mounts.map((m) => [m.id, m.name] as const)
        );

        const updatedExisting = design.furniture
          .filter((f) => !removeMap.has(f.id))
          .map((f) => {
            const u = updateMap.get(f.id);
            if (!u) return f;
            return {
              ...f,
              x: u.x,
              y: u.y,
              rotation: u.rotation,
              rationale: {
                reason: u.reason,
                source: "feng-shui" as const,
                consultationId,
                at,
              },
            };
          });

        const newAdditions: Furniture[] = suggestion.additions.map((a) => {
          const id = uid("f");
          const { reason, ...rest } = a;
          return {
            ...rest,
            id,
            rationale: {
              reason,
              source: "feng-shui" as const,
              consultationId,
              at,
            },
          };
        });

        const mountUpdatesIn = suggestion.mountUpdates ?? [];
        const mountAdditionsIn = suggestion.mountAdditions ?? [];
        const mountRemovalsIn = suggestion.mountRemovals ?? [];
        const mountRemoveMap = new Map(
          mountRemovalsIn.map((r) => [r.id, r] as const)
        );
        const mountUpdateMap = new Map(
          mountUpdatesIn.map((u) => [u.id, u] as const)
        );

        const updatedMounts = design.mounts
          .filter((m) => !mountRemoveMap.has(m.id))
          .map((m) => {
            const u = mountUpdateMap.get(m.id);
            if (!u) return m;
            return {
              ...m,
              wallIndex: u.wallIndex,
              alongWall: u.alongWall,
              fromFloor: u.fromFloor,
              rationale: {
                reason: u.reason,
                source: "feng-shui" as const,
                consultationId,
                at,
              },
            };
          });

        const newMountAdditions: WallMount[] = mountAdditionsIn.map((a) => {
          const id = uid("m");
          const { reason, ...rest } = a;
          return {
            ...rest,
            id,
            rationale: {
              reason,
              source: "feng-shui" as const,
              consultationId,
              at,
            },
          };
        });

        const appliedUpdates = suggestion.updates
          .filter((u) => nameById.has(u.id) && !removeMap.has(u.id))
          .map((u) => ({
            furnitureId: u.id,
            furnitureName: nameById.get(u.id) ?? "Untitled piece",
            reason: u.reason,
          }));
        const appliedAdditions = suggestion.additions.map((a, i) => ({
          furnitureId: newAdditions[i].id,
          furnitureName: a.name,
          reason: a.reason,
        }));
        const appliedRemovals = suggestion.removals
          .filter((r) => nameById.has(r.id))
          .map((r) => ({
            furnitureName: nameById.get(r.id) ?? "Untitled piece",
            reason: r.reason,
          }));
        const appliedMountUpdates = mountUpdatesIn
          .filter((u) => mountNameById.has(u.id) && !mountRemoveMap.has(u.id))
          .map((u) => ({
            mountId: u.id,
            mountName: mountNameById.get(u.id) ?? "Untitled mount",
            reason: u.reason,
          }));
        const appliedMountAdditions = mountAdditionsIn.map((a, i) => ({
          mountId: newMountAdditions[i].id,
          mountName: a.name,
          reason: a.reason,
        }));
        const appliedMountRemovals = mountRemovalsIn
          .filter((r) => mountNameById.has(r.id))
          .map((r) => ({
            mountName: mountNameById.get(r.id) ?? "Untitled mount",
            reason: r.reason,
          }));

        const consultation: FengShuiConsultation = {
          id: consultationId,
          at,
          mode: suggestion.consultation.mode,
          overall: suggestion.consultation.overall,
          applied: {
            updates: appliedUpdates,
            additions: appliedAdditions,
            removals: appliedRemovals,
          },
        };
        if (appliedMountUpdates.length > 0) {
          consultation.applied.mountUpdates = appliedMountUpdates;
        }
        if (appliedMountAdditions.length > 0) {
          consultation.applied.mountAdditions = appliedMountAdditions;
        }
        if (appliedMountRemovals.length > 0) {
          consultation.applied.mountRemovals = appliedMountRemovals;
        }
        if (suggestion.consultation.notes) {
          consultation.notes = suggestion.consultation.notes;
        }
        if (suggestion.consultation.filtered) {
          consultation.filtered = suggestion.consultation.filtered;
        }

        commitDesign(
          {
            ...design,
            furniture: [...updatedExisting, ...newAdditions],
            mounts: [...updatedMounts, ...newMountAdditions],
            consultations: [...(design.consultations ?? []), consultation],
          },
          { selectedFurnitureId: null, selectedMountId: null }
        );
        for (const f of newAdditions) {
          track("furniture_added", { preset_name: f.name, source: "ai" });
        }
        if (suggestion.example) {
          track("feng_shui_example_applied", {
            updates: appliedUpdates.length + appliedMountUpdates.length,
          });
        } else {
          track("ai_layout_applied", {
            mode: suggestion.consultation.mode,
            updates: appliedUpdates.length + appliedMountUpdates.length,
            additions: appliedAdditions.length + appliedMountAdditions.length,
            removals: appliedRemovals.length + appliedMountRemovals.length,
          });
        }
        return consultationId;
      },

      removeConsultation: (id) => {
        const { design } = get();
        if (!(design.consultations ?? []).some((c) => c.id === id)) return;
        commitDesign({
          ...design,
          consultations: (design.consultations ?? []).filter((c) => c.id !== id),
        });
      },

      appendRanking: (ranking) => {
        const { design } = get();
        commitDesign({
          ...design,
          rankings: [...(design.rankings ?? []), ranking],
        });
      },

      removeRanking: (id) => {
        const { design } = get();
        if (!(design.rankings ?? []).some((r) => r.id === id)) return;
        commitDesign({
          ...design,
          rankings: (design.rankings ?? []).filter((r) => r.id !== id),
        });
      },

      flashFurnitureIds: (ids, durationMs = 1500) => {
        if (ids.length === 0) return;
        set({ flashedFurnitureIds: ids });
        // Auto-clear so a long-lived flash doesn't outlive its UI trigger.
        if (typeof window !== "undefined") {
          window.setTimeout(() => {
            const current = get().flashedFurnitureIds;
            // Only clear if nobody else has flashed something newer.
            if (
              current.length === ids.length &&
              current.every((id, i) => id === ids[i])
            ) {
              set({ flashedFurnitureIds: [] });
            }
          }, durationMs);
        }
      },

      setPerson: (p) => {
        const { design } = get();
        const next = { ...design.person, ...p };
        if (sameJson(design.person, next)) return;
        commitDesign({ ...design, person: next });
      },
      toggleSnap: () => set((s) => ({ snapEnabled: !s.snapEnabled })),
      setGridSnapInches: (n) => {
        if (!isAllowedGridSnap(n)) return;
        set({ gridSnapInches: n });
      },
      toggleFurnitureSnap: () =>
        set((s) => ({ furnitureSnapEnabled: !s.furnitureSnapEnabled })),
      togglePerson: () => set((s) => ({ showPerson: !s.showPerson })),

      beginGesture: (opts) => {
        if (historyGestureDepth === 0) {
          historyGestureBaseline = structuredClone(get().design);
          historyGestureBlockUndo = Boolean(opts?.blockUndo);
        } else if (opts?.blockUndo) {
          historyGestureBlockUndo = true;
        }
        historyGestureDepth++;
      },
      endGesture: () => {
        closeGesture();
      },
      cancelGesture: () => {
        if (historyGestureDepth <= 0) return;
        if (historyGestureDepth > 1) {
          historyGestureDepth--;
          return;
        }
        const baseline = historyGestureBaseline;
        discardHistoryGesture();
        if (!baseline || sameJson(baseline, get().design)) {
          set({ historyPending: false });
          return;
        }
        set({
          design: baseline,
          historyPending: false,
          ...survivingSelection(baseline),
        });
      },
      batch: (fn) => {
        get().beginGesture();
        try {
          fn();
        } finally {
          get().endGesture();
        }
      },
      coalesceEdit: (fn) => {
        if (historyGestureDepth === 0) {
          get().beginGesture();
          coalesceOwnsGesture = true;
        }
        fn();
        if (!coalesceOwnsGesture) return;
        clearCoalesceTimer();
        coalesceTimer = setTimeout(() => {
          coalesceTimer = null;
          get().endGesture();
        }, NUDGE_GESTURE_MS);
      },
      undo: () => {
        if (historyGestureDepth > 0 && historyGestureBlockUndo) return;
        const reopen = historyGestureDepth > 0 && !coalesceOwnsGesture;
        commitOpenGesture();
        const { undoStack, design, redoStack } = get();
        if (undoStack.length === 0) return;
        noteUndo(undoStack.length);
        const entry = undoStack[undoStack.length - 1];
        const next = applyHistoryPatch(design, entry.undo);
        set({
          design: next,
          undoStack: undoStack.slice(0, -1),
          redoStack: [...redoStack, entry],
          historyPending: false,
          ...survivingSelection(next),
        });
        const finish = finishUndoSurface(design.room, next.room);
        if (finish) track("finish_undo", { surface: finish });
        if (reopen) get().beginGesture();
      },
      redo: () => {
        if (historyGestureDepth > 0 && historyGestureBlockUndo) return;
        const reopen = historyGestureDepth > 0 && !coalesceOwnsGesture;
        commitOpenGesture();
        const { redoStack, design, undoStack } = get();
        if (redoStack.length === 0) return;
        const entry = redoStack[redoStack.length - 1];
        const next = applyHistoryPatch(design, entry.redo);
        set({
          design: next,
          redoStack: redoStack.slice(0, -1),
          undoStack: [...undoStack, entry],
          historyPending: false,
          ...survivingSelection(next),
        });
        if (reopen) get().beginGesture();
      },

      applyRemoteDesign: (incoming) => {
        if (historyGestureDepth > 0 && historyGestureBaseline) {
          const rebased = rebaseGestureDesign(
            historyGestureBaseline,
            get().design,
            incoming
          );
          historyGestureBaseline = rebased.baseline;
          set({ design: rebased.current });
          return;
        }
        const adjusting = get().adjustingFurnitureId;
        set({
          design: incoming,
          ...(adjusting && !incoming.furniture.some((f) => f.id === adjusting)
            ? { adjustingFurnitureId: leaveAdjust("deleted") }
            : {}),
        });
      },

      copySelection: () => {
        const { selectedFurnitureId, selectedMountId, design } = get();
        if (selectedFurnitureId) {
          const item = design.furniture.find((f) => f.id === selectedFurnitureId);
          if (!item) return false;
          set({ clipboard: { kind: "furniture", item: payloadWithoutId(item) } });
          return true;
        }
        if (selectedMountId) {
          const item = design.mounts.find((m) => m.id === selectedMountId);
          if (!item) return false;
          set({ clipboard: { kind: "mount", item: payloadWithoutId(item) } });
          return true;
        }
        return false;
      },
      pasteClipboard: () => {
        const clip = get().clipboard;
        if (!clip) return false;
        if (clip.kind === "furniture") {
          const payload = structuredClone(clip.item);
          payload.x += PASTE_OFFSET_IN;
          payload.y += PASTE_OFFSET_IN;
          const footprint = resolveFurniture(payload).footprint;
          const clamped = clampPoseInRoom(
            { x: payload.x, y: payload.y },
            footprint,
            payload.rotation,
            get().design.room
          );
          payload.x = clamped.x;
          payload.y = clamped.y;
          get().addFurniture(payload, { source: "duplicate" });
          set({ clipboard: { kind: "furniture", item: payload } });
          return true;
        }
        const payload = structuredClone(clip.item);
        payload.alongWall += PASTE_OFFSET_IN;
        const { room } = get().design;
        const wallLen = wallLength(room.points, payload.wallIndex);
        const halfW = payload.width / 2;
        const halfH = payload.height / 2;
        payload.alongWall = Math.max(halfW, Math.min(wallLen - halfW, payload.alongWall));
        payload.fromFloor = Math.max(
          halfH,
          Math.min(room.ceilingHeight - halfH, payload.fromFloor)
        );
        get().addMount(payload);
        set({ clipboard: { kind: "mount", item: payload } });
        return true;
      },

      loadDesign: (design, savedRoomId = null) => {
        // Backfill optional collections the same way the persist `migrate`
        // step does for the active design. SavedRooms in the library aren't
        // versioned alongside the editor store, so a layout saved before
        // `consultations` / `openings` existed can still land here with
        // those fields `undefined`. Leaving them undefined would force every
        // selector to coalesce via `?? []`, and any selector that did so by
        // returning a fresh `[]` literal would trip React 19's
        // useSyncExternalStore snapshot cache ("Maximum update depth
        // exceeded"). Normalizing once at the boundary keeps consumers
        // simple and the bug from recurring.
        const cloned = normalizeDesign(structuredClone(design));
        discardHistoryGesture();
        set({
          design: cloned,
          activeSavedRoomId: savedRoomId,
          // Switching to a private library room exits any active shared
          // session — keeps the two lifecycles cleanly mutually exclusive.
          activeSharedRoomId: null,
          view: "design",
          activeWallIndex: null,
          activeInteriorWall: null,
          selectedFurnitureId: null,
          selectedMountId: null,
          selectedOpeningId: null,
          selectedRoomWallIndex: null,
          selectedRoomVertexIndex: null,
          selectedInteriorWall: null,
          selectedInteriorVertexId: null,
          // A loaded room is by definition no longer the default rectangle, so
          // drop the user into Edit mode and clear any stale sketch state.
          // Locks travel inside `cloned.room.lockedWallLengths` already, so
          // nothing extra needed here.
          buildTool: isUnstartedRoom(cloned.room) ? "sketch" : "edit",
          designTool: "select",
          pendingSketch: null,
          editingModulesId: null,
          adjustingFurnitureId: leaveAdjust("view"),
          undoStack: [],
          redoStack: [],
          historyPending: false,
          draggingFurnitureId: null,
          draggingMountId: null,
          draggingOpeningId: null,
        });
      },
      clearActiveSavedRoom: () => set({ activeSavedRoomId: null }),

      loadSharedDesign: (design, sharedRoomId) => {
        // Note: deliberately NOT upgrading legacy furniture here. Adding
        // `model`/`material`/`frontKind` on load would diff against the cloud
        // doc and fan out a write per piece the moment a room opens. The
        // resolver renders legacy pieces identically at read time; the
        // fields get persisted naturally the next time each piece is edited.
        const cloned = structuredClone(design);
        if (!Array.isArray(cloned.openings)) cloned.openings = [];
        if (!Array.isArray(cloned.consultations)) cloned.consultations = [];
        if (!Array.isArray(cloned.rankings)) cloned.rankings = [];
        discardHistoryGesture();
        set({
          design: cloned,
          activeSavedRoomId: null,
          activeSharedRoomId: sharedRoomId,
          view: "design",
          activeWallIndex: null,
          activeInteriorWall: null,
          selectedFurnitureId: null,
          selectedMountId: null,
          selectedOpeningId: null,
          selectedRoomWallIndex: null,
          selectedRoomVertexIndex: null,
          selectedInteriorWall: null,
          selectedInteriorVertexId: null,
          buildTool: isUnstartedRoom(cloned.room) ? "sketch" : "edit",
          designTool: "select",
          pendingSketch: null,
          editingModulesId: null,
          adjustingFurnitureId: leaveAdjust("view"),
          undoStack: [],
          redoStack: [],
          historyPending: false,
          draggingFurnitureId: null,
          draggingMountId: null,
          draggingOpeningId: null,
        });
      },
      clearActiveSharedRoom: () => {
        discardHistoryGesture();
        set({
          activeSharedRoomId: null,
          draggingFurnitureId: null,
          draggingMountId: null,
          draggingOpeningId: null,
          undoStack: [],
          redoStack: [],
          historyPending: false,
        });
      },

      setDraggingFurniture: (id) => set({ draggingFurnitureId: id }),
      setDraggingMount: (id) => set({ draggingMountId: id }),
      setDraggingOpening: (id) => set({ draggingOpeningId: id }),

      resetAll: () => {
        discardHistoryGesture();
        set({
          design: defaultDesign(),
          activeSavedRoomId: null,
          activeSharedRoomId: null,
          draggingFurnitureId: null,
          draggingMountId: null,
          draggingOpeningId: null,
          view: "build",
          activeWallIndex: null,
          activeInteriorWall: null,
          selectedFurnitureId: null,
          selectedMountId: null,
          selectedOpeningId: null,
          selectedRoomWallIndex: null,
          selectedRoomVertexIndex: null,
          selectedInteriorWall: null,
          selectedInteriorVertexId: null,
          buildTool: "sketch",
          designTool: "select",
          pendingSketch: null,
          editingModulesId: null,
          adjustingFurnitureId: leaveAdjust("view"),
          undoStack: [],
          redoStack: [],
          historyPending: false,
          clipboard: null,
        });
      },
      };
    },
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      partialize: (s) => ({
        design: s.design,
        activeSavedRoomId: s.activeSavedRoomId,
        // Persisted so a reload of a shared session resumes in shared
        // mode instead of silently degrading to private (which loses
        // the multiplayer banner, presence, and Firestore sync). On
        // boot, `App.tsx` re-verifies membership against the shared
        // rooms registry — if the user is no longer in `members`, the
        // upgrade is skipped and we fall through to private editing.
        activeSharedRoomId: s.activeSharedRoomId,
        view: s.view,
        snapEnabled: s.snapEnabled,
        gridSnapInches: s.gridSnapInches,
        furnitureSnapEnabled: s.furnitureSnapEnabled,
        showPerson: s.showPerson,
      }),
      migrate: (persisted, _version) => {
        // Inject `openings: []` into pre-v2 payloads so existing users don't
        // lose their saved room when the schema gains the new field. v3
        // additionally defaults `consultations: []` so the optional history
        // log reads consistently regardless of when the design was created.
        // v4 defaults `rankings: []` so the new ranking history log behaves
        // the same way for designs persisted before the field existed.
        // v5 upgrades legacy furniture to the parametric model (explicit
        // `model` / `material` / `frontKind`) so old sofas render as sofas.
        const p = persisted as { design?: Partial<Design> } | undefined;
        if (p?.design) {
          normalizeDesign(p.design as Design);
        }
        return p;
      },
      // `buildTool` isn't persisted (UI-only). After rehydration we re-derive
      // the default from the now-loaded room: a user who already shaped their
      // room shouldn't land in Sketch mode and risk overwriting it.
      //
      // `room.lockedWallLengths` IS persisted (it's part of `design.room`) —
      // typed-in measurements survive page reloads, library saves, and
      // cross-device Firestore sync.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.buildTool = isUnstartedRoom(state.design.room) ? "sketch" : "edit";
        state.designTool = "select";
        state.pendingSketch = null;
        if (!isAllowedGridSnap(state.gridSnapInches)) {
          state.gridSnapInches = DEFAULT_GRID_SNAP_IN;
        }
        if (typeof state.furnitureSnapEnabled !== "boolean") {
          state.furnitureSnapEnabled = true;
        }
        state.historyPending = false;
        state.undoStack = [];
        state.redoStack = [];
      },
    }
  )
);
